import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Zap, Target, TrendingUp, User, LogOut, AlertTriangle, RefreshCw, ArrowLeft, LifeBuoy, ShieldCheck, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/JourneyChrome";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Home", icon: Home, tid: "home" },
  { to: "/dashboard/sprint", label: "My Edit", icon: Zap, tid: "my-sprint" },
  { to: "/dashboard/goal", label: "My Goal", icon: Target, tid: "my-goal" },
  { to: "/dashboard/progress", label: "Progress", icon: TrendingUp, tid: "progress" },
  { to: "/dashboard/profile", label: "Profile", icon: User, tid: "profile" },
];

// Admin/Owner accounts WITHOUT a customer entitlement live entirely in the admin
// environment: their nav and "home" are the admin hub, never the customer
// dashboard (which would bounce them through the entitlement gate to /login).
function isAdminOnly(role, entitled) {
  return (role === "owner" || role === "admin") && !entitled;
}
function adminHome() {
  return "/dashboard/admin/analytics";
}
function navFor(role, entitled) {
  if (!isAdminOnly(role, entitled)) return NAV;
  const items = [
    { to: "/dashboard/admin/analytics", label: "Admin Home", icon: BarChart3, tid: "admin-home" },
    { to: "/dashboard/admin/support", label: "Support", icon: LifeBuoy, tid: "admin-support" },
  ];
  if (role === "owner") items.push({ to: "/dashboard/admin/access", label: "Admin access", icon: ShieldCheck, tid: "admin-access" });
  return items;
}

// Per-user dashboard shell. Architected so the future Money Landed flow can be
// added without replacing the shell. Nav: Home | My Sprint | My Goal | Progress | Profile.
export function DashboardShell({ children, wide = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, entitled, logout } = useAuth();
  const nav = navFor(role, entitled);
  const isActive = (to) => (to === "/dashboard" ? location.pathname === to : location.pathname.startsWith(to));

  const doSignOut = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#FAF8F4] text-[#171717]" data-testid="dashboard-shell">
      <ScrollToTop />
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-[#ECE6DC] bg-[#FCFBF8] px-5 py-7 lg:flex">
        <BrandMark />
        <nav className="mt-12 flex-1 space-y-1.5">
          {nav.map(({ to, label, icon: Icon, tid }) => (
            <Link
              key={to}
              to={to}
              data-testid={`nav-${tid}`}
              className={`relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
                isActive(to) ? "bg-[#DDE7DF] text-[#164C3B]" : "text-[#6B6256] hover:bg-[#F4F0E8] hover:text-[#164C3B]"
              }`}
            >
              {isActive(to) && <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-[#C9A86A]" aria-hidden="true" />}
              <Icon className="h-4.5 w-4.5" /> {label}
            </Link>
          ))}
        </nav>
        <div className="border-t border-[#ECE6DC] pt-4">
          <p className="truncate px-1 text-sm font-medium text-[#171717]">{user?.first_name} {user?.last_name}</p>
          <p className="truncate px-1 text-xs text-[#A89A88]">{user?.email}</p>
          <button onClick={doSignOut} data-testid="logout-button" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#70584A] hover:text-[#164C3B]">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="flex h-16 items-center justify-between border-b border-[#ECE6DC] bg-[#FCFBF8] px-5 lg:hidden">
        <BrandMark />
        <button onClick={doSignOut} data-testid="logout-button-mobile" className="inline-flex items-center gap-1.5 rounded-full border border-[#ECE6DC] px-3 py-1.5 text-sm text-[#525252]">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </header>

      {/* Content */}
      <main className="px-5 pb-[calc(8rem+env(safe-area-inset-bottom))] pt-6 sm:px-8 lg:ml-64 lg:px-10 lg:pb-12 lg:pt-10 xl:px-14">
        <div className={`mx-auto w-full ${wide ? "max-w-6xl" : "max-w-3xl"}`}>
          {children}
          <DashboardFooter />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-[#ECE6DC] bg-[#FCFBF8]/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden" data-testid="mobile-bottom-nav">
        {nav.map(({ to, label, icon: Icon, tid }) => {
          const active = isActive(to);
          return (
            <Link
              key={to}
              to={to}
              data-testid={`mobile-nav-${tid}`}
              className="relative flex min-w-0 flex-1 flex-col items-center gap-1 px-0.5 py-2 text-center text-[10px] font-medium leading-tight"
            >
              {active && <span className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-[#164C3B]" aria-hidden="true" />}
              <Icon className={`h-5 w-5 shrink-0 ${active ? "text-[#164C3B]" : "text-[#A89A88]"}`} />
              <span className={`w-full truncate ${active ? "font-semibold text-[#164C3B]" : "text-[#A89A88]"}`}>{label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export function DashboardFooter() {
  const location = useLocation();
  return (
    <footer className="mt-12 w-full border-t border-[#ECE6DC] pt-6" data-testid="dashboard-footer">
      <p className="text-xs leading-relaxed text-[#A89A88]">
        The Paycheck Edit™ provides educational financial planning tools, calculations, projections, and proposed money
        moves based on the information you provide. It is not financial, investment, legal, tax, accounting, or credit
        advice, and it never moves your money. You decide and confirm every move. Results vary and are not guaranteed.{" "}
        <Link to="/legal#disclaimer" state={{ from: location.pathname }} data-testid="footer-disclaimer" className="font-medium text-[#70584A] underline hover:text-[#164C3B]">Read the full Financial Disclaimer.</Link>
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-[#70584A]">
        <Link to="/legal#privacy" state={{ from: location.pathname }} data-testid="footer-privacy" className="hover:text-[#164C3B]">Privacy</Link>
        <Link to="/legal#terms" state={{ from: location.pathname }} data-testid="footer-terms" className="hover:text-[#164C3B]">Terms</Link>
        <Link to="/dashboard/help" state={{ from: location.pathname }} data-testid="footer-support" className="hover:text-[#164C3B]">Support</Link>
        <span className="ml-auto text-[#C4B8A6]">© 2026 The Paycheck Edit™</span>
      </div>
    </footer>
  );
}

export function PageHead({ title, sub, eyebrow, backToHome, homeTo }) {
  const { role, entitled } = useAuth();
  const home = homeTo || (isAdminOnly(role, entitled) ? adminHome() : "/dashboard");
  return (
    <div className="mb-7 lg:mb-9">
      {backToHome && (
        <Link to={home} data-testid="page-back-home" className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#70584A] transition-colors hover:text-[#164C3B]">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
      )}
      {eyebrow && <p className="pe-eyebrow mb-2.5">{eyebrow}</p>}
      <h1 className="font-serif text-[2rem] leading-[1.08] text-[#171717] sm:text-[2.6rem]">{title}</h1>
      {sub && <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[#525252]">{sub}</p>}
    </div>
  );
}

// Shown when a data request FAILS. Never confuse this with an empty state:
// the customer's data is safe on the server, the request just did not complete.
export function ErrorState({ title = "We couldn't load this right now", body = "Something interrupted the connection. Your information is safe. Give it another try.", onRetry, testid = "data-error" }) {
  return (
    <div className="rounded-2xl border border-[#E7D4CE] bg-[#FBF1EE] p-10 text-center" data-testid={testid}>
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#F2DED7] text-[#8B3A2B]">
        <AlertTriangle className="h-7 w-7" />
      </span>
      <p className="mt-5 font-serif text-2xl text-[#171717]">{title}</p>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[#70584A]">{body}</p>
      {onRetry && (
        <Button onClick={onRetry} data-testid={`${testid}-retry`} className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643]">
          <RefreshCw className="h-4 w-4" /> Try again
        </Button>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, body, testid }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#D9D1C4] bg-white/60 p-10 text-center" data-testid={testid}>
      {Icon && (
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]">
          <Icon className="h-7 w-7" />
        </span>
      )}
      <p className="mt-5 font-serif text-2xl text-[#171717]">{title}</p>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[#70584A]">{body}</p>
    </div>
  );
}
